import type { Material, MaterialCategory } from "@/types";
import { recomputeAll } from "@/services/pricing";

// Catalogue of real-ish Indian cabling distribution SKUs.
// We cycle through this list to synthesise 180 deterministic material lines.
// Categories drive the brand-lock rule (copper→CommScope, fiber→Corning).

type CatalogItem = {
  partNo: string;
  description: string;
  spec: string;
  oem: "CommScope" | "Corning" | "Panduit" | "Belden";
  uom: "Box" | "Nos" | "Mtr" | "Reel";
  listPrice: number;
  imported: boolean;
  category: MaterialCategory;
};

// Base catalogue - all rows are compliant by default (copper→CommScope,
// fiber→Corning, accessory→any). Six specific lines are then overridden
// to break the rule in BRAND_OVERRIDES below.
const CATALOG: CatalogItem[] = [
  { partNo: "CS-C6A-UTP-BOX",   description: "Cat6A UTP Cable 305m Box",     spec: "23AWG, LSZH, Blue",     oem: "CommScope", uom: "Box", listPrice: 42800, imported: false, category: "copper"    },
  { partNo: "CS-C6A-PC2M",      description: "Cat6A Patch Cord 2m",           spec: "26AWG, LSZH, Blue",     oem: "CommScope", uom: "Nos", listPrice:   780, imported: true,  category: "copper"    },
  { partNo: "CS-C6A-KJ",        description: "Cat6A Keystone Jack",           spec: "T568A/B, Toolless",     oem: "CommScope", uom: "Nos", listPrice:   640, imported: false, category: "copper"    },
  { partNo: "CS-C6A-PP24",      description: "24-Port Cat6A Patch Panel",     spec: "1U, Shielded",          oem: "CommScope", uom: "Nos", listPrice: 14200, imported: true,  category: "copper"    },
  { partNo: "CG-OM4-12C-2KM",   description: "OM4 Fiber Cable 12-Core",       spec: "50/125um, Armoured",    oem: "Corning",   uom: "Mtr", listPrice:   320, imported: true,  category: "fiber"     },
  { partNo: "CG-LC-LC-OM4-3M",  description: "LC-LC Duplex Patch Cord OM4",   spec: "3m, Aqua, LSZH",        oem: "Corning",   uom: "Nos", listPrice:  1450, imported: true,  category: "fiber"     },
  { partNo: "CG-LIU-24",        description: "24-Port Fiber LIU",             spec: "1U, LC, Rack Mount",    oem: "Corning",   uom: "Nos", listPrice: 18400, imported: true,  category: "fiber"     },
  { partNo: "PD-VCM-42U",       description: "Vertical Cable Manager 42U",    spec: "Double sided, Black",   oem: "Panduit",   uom: "Nos", listPrice: 22800, imported: true,  category: "accessory" },
  { partNo: "CS-C6-UTP-BOX",    description: "Cat6 UTP Cable 305m Box",       spec: "24AWG, PVC, Grey",      oem: "CommScope", uom: "Box", listPrice: 18900, imported: false, category: "copper"    },
  { partNo: "CS-C6-PC1M",       description: "Cat6 Patch Cord 1m",            spec: "26AWG, LSZH, Grey",     oem: "CommScope", uom: "Nos", listPrice:   320, imported: true,  category: "copper"    },
  { partNo: "BD-FO-SPLICE-24",  description: "24-Fiber Splice Tray",          spec: "Ceramic sleeves",       oem: "Belden",    uom: "Nos", listPrice:  6800, imported: false, category: "accessory" },
  { partNo: "CG-OM4-24C-2KM",   description: "OM4 Fiber Cable 24-Core",       spec: "50/125um, Armoured",    oem: "Corning",   uom: "Mtr", listPrice:   540, imported: true,  category: "fiber"     },
  { partNo: "CS-C6A-STP-BOX",   description: "Cat6A STP Cable 305m Box",      spec: "23AWG, LSZH, Screened", oem: "CommScope", uom: "Box", listPrice: 58200, imported: false, category: "copper"    },
  { partNo: "CS-C6A-KJ-SH",     description: "Cat6A Shielded Keystone Jack",  spec: "T568A/B, Metal Body",   oem: "CommScope", uom: "Nos", listPrice:   890, imported: true,  category: "copper"    },
  { partNo: "CS-C6-PP48",       description: "48-Port Cat6 Patch Panel",      spec: "2U, Unshielded",        oem: "CommScope", uom: "Nos", listPrice: 16800, imported: true,  category: "copper"    },
  { partNo: "CG-LC-LC-OM3-2M",  description: "LC-LC Duplex Patch Cord OM3",   spec: "2m, Aqua, LSZH",        oem: "Corning",   uom: "Nos", listPrice:   980, imported: true,  category: "fiber"     },
  { partNo: "PD-HCM-1U",        description: "Horizontal Cable Manager 1U",   spec: "D-Ring, Black",         oem: "Panduit",   uom: "Nos", listPrice:  2400, imported: true,  category: "accessory" },
  { partNo: "BD-CT-100",        description: "Cable Tray 100mm 2.5m",         spec: "Powder coated",         oem: "Belden",    uom: "Nos", listPrice:   720, imported: false, category: "accessory" },
  { partNo: "CG-SC-SC-SM-5M",   description: "SC-SC Duplex Patch Cord SM",    spec: "5m, Yellow, OS2",       oem: "Corning",   uom: "Nos", listPrice:  1180, imported: true,  category: "fiber"     },
  { partNo: "CS-FBP-24",        description: "24-Port Face Plate + Back Box", spec: "British Std, White",    oem: "CommScope", uom: "Nos", listPrice:  4200, imported: false, category: "accessory" },
];

// Six deliberate brand-lock violations. Each overrides the OEM of one
// specific lineNo to a brand that breaks the client's rule.
const BRAND_OVERRIDES: Record<number, "CommScope" | "Corning" | "Panduit" | "Belden"> = {
  2:   "Panduit",  // copper (Cat6A Patch Cord)         → must be CommScope
  25:  "Belden",   // fiber  (OM4 Fiber Cable 12-Core)  → must be Corning
  53:  "Panduit",  // copper (Cat6A STP Cable)          → must be CommScope
  76:  "Panduit",  // fiber  (LC-LC OM3)                → must be Corning
  109: "Belden",   // copper (Cat6 UTP)                 → must be CommScope
  142: "Belden",   // copper (Cat6A Patch Cord)         → must be CommScope
};

// 25 rows with warehouse stock, quantities kept in 40..800 deterministically.
const STOCK: Record<number, { qty: number; warehouse: string }> = {};
for (let k = 0; k < 25; k++) {
  const lineNo = 3 + k * 7; // 3, 10, 17, ..., 171
  const qty = 40 + ((k * 71) % 761);
  STOCK[lineNo] = { qty, warehouse: "Bangalore Warehouse" };
}

const ORIGINS_IMPORTED = ["US Factory", "China"] as const;
const ORIGINS_LOCAL    = ["Goa Plant", "Local Stock"] as const;

function pick<T>(arr: readonly T[], seed: number): T {
  return arr[Math.abs(seed) % arr.length];
}

function makeLine(i: number): Material {
  const cat = CATALOG[i % CATALOG.length];
  const lineNo = i + 1;

  const qty =
    cat.uom === "Box"  ? 4 + (i % 12) :
    cat.uom === "Mtr"  ? 120 + (i * 37) % 880 :
    cat.uom === "Reel" ? 2 + (i % 6) :
                         24 + (i * 13) % 220;

  const discountPct = 18 + ((i * 7) % 25);
  const marginPct   = 8  + ((i * 11) % 13);
  const origin      = cat.imported ? pick(ORIGINS_IMPORTED, i) : pick(ORIGINS_LOCAL, i);
  const oem         = BRAND_OVERRIDES[lineNo] ?? cat.oem;
  const stockEntry  = STOCK[lineNo];

  const raw: Material = {
    lineNo,
    partNo: cat.partNo,
    description: cat.description,
    spec: cat.spec,
    oem,
    qty,
    uom: cat.uom,
    listPrice: cat.listPrice,
    discountPct,
    netPrice: 0,
    origin,
    landedCost: 0,
    marginPct,
    sellPrice: 0,
    lineTotal: 0,
    category: cat.category,
    stock: stockEntry?.qty,
    warehouse: stockEntry?.warehouse,
  };
  return raw;
}

// Emit a fully-computed material list so the initial render is coherent
// without another pass.
//
// It took a PricingContext and a mode until BF5d-3, and the mode had to be
// "import" here because that was the branch which left this module-level seed
// data untouched -- there being no quote, and therefore no real mode, in
// scope. recomputeAll now derives netPrice and the placeholder chain from the
// row alone, so there is nothing left to choose.
export const mockMaterials: Material[] = recomputeAll(
  Array.from({ length: 180 }, (_, i) => makeLine(i)),
);
